import { useEffect, useMemo, useState } from "react";
import { message } from "antd";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import {
  buildProfitabilityDetailsParams,
  mapTopSportsRows,
  useProfitabilityTopSports,
  useProfitabilityTopSportsExport,
  type ProfitabilityMetricScope,
} from "../../../queries/sportsbook/profitabilityDetails";
import type { ReportingSummaryFiltersValue } from "../../../queries/sportsbook/reportingSummaryFilterOptions";
import { triggerBrowserDownload } from "../../../utils/triggerBrowserDownload";
import { getProfitabilitySportsColumns } from "../profitabilityReportingColumns";
import ProfitabilityTableSection from "./ProfitabilityTableSection";
import {
  useProfitabilityFilterOptions,
  useProfitabilitySectionFilterState,
} from "./useProfitabilitySectionFilters";

type Props = {
  date: string;
  inheritedFilters: ReportingSummaryFiltersValue;
};

function ProfitabilitySportsSection({ date, inheritedFilters }: Props) {
  const currencySymbol = useCurrencySymbol() ?? "$";
  const [metricScope, setMetricScope] =
    useState<ProfitabilityMetricScope>("total");
  const { filters, setFilters, setBetType, setMoneyType } =
    useProfitabilitySectionFilterState(inheritedFilters);
  const params = useMemo(() => ({ date, ...filters }), [date, filters]);
  const query = useProfitabilityTopSports(params);
  const options = useProfitabilityFilterOptions(
    query.data?.filters ?? [],
    setFilters,
    date,
  );
  const rows = useMemo(
    () => mapTopSportsRows(query.data?.rows ?? [], metricScope),
    [query.data?.rows, metricScope],
  );
  const exportMutation = useProfitabilityTopSportsExport();
  const columns = useMemo(
    () => getProfitabilitySportsColumns(currencySymbol),
    [currencySymbol],
  );

  useEffect(() => {
    if (query.isError) {
      message.error(AppStrings.REPORTING_SUMMARY_FETCH_ERROR);
    }
  }, [query.isError]);

  const exportState = {
    isBusy: exportMutation.isPending,
    start: (payload: Record<string, unknown>) =>
      exportMutation.mutate(payload, {
        onSuccess: (download) => {
          if (download?.download_url) {
            triggerBrowserDownload(download.download_url);
            return;
          }
          message.error(AppStrings.REPORTING_SUMMARY_EXPORT_ERROR);
        },
        onError: () =>
          message.error(AppStrings.REPORTING_SUMMARY_EXPORT_ERROR),
      }),
  };

  return (
    <ProfitabilityTableSection
      id="reporting-profitability-sports"
      title={AppStrings.SPORTSBOOK_PROFITABILITY_SPORTS}
      rows={rows}
      columns={columns}
      paginated
      loading={query.isLoading || query.isFetching}
      metricScope={metricScope}
      onMetricScopeChange={setMetricScope}
      betType={filters.bet_type}
      moneyType={filters.money_type}
      betTypeOptions={options?.bet_type}
      moneyTypeOptions={options?.money_type}
      onBetTypeChange={setBetType}
      onMoneyTypeChange={setMoneyType}
      exportState={exportState}
      buildExportPayload={() => buildProfitabilityDetailsParams(params)}
    />
  );
}

export default ProfitabilitySportsSection;
